import { Fragment, useEffect, useMemo, useRef } from "react"

import { Col, Row } from "antd"
import { useDispatch, useSelector } from "react-redux"

import EmptyComponent from "../../Components/Empty"
import ChannelDonut from "./components/ChannelDonut/ChannelDonut"
import ChannelTrendsChart from "./components/ChannelTrendsChart"
import CountriesPanel from "./components/CountriesPanel/CountriesPanel"
import DeflectFilters from "./components/DeflectFilters"
import KpiTabs from "./components/KpiTabs/KpiTabs"
import LookupFlow from "./components/LookupFlow"
import MatchRateMatrix from "./components/MatchRateMatrix"
import ProgramPanel from "./components/ProgramPanel/ProgramPanel"
import QueueCards from "./components/QueueCards/QueueCards"
import RecentLookups from "./components/RecentLookups/RecentLookups"
import RepeatCardholdersPanel from "./components/RepeatCardholdersPanel"
import { useDeflectScope } from "./hooks/useDeflectScope"
import { DEFLECT_SECTION } from "./redux/deflectAnalytics.constants"
import {
  resetDeflectData,
  selectDeflectFetching,
  selectDeflectFilters,
  selectDeflectSection
} from "./redux/deflectAnalytics.slice"
import { rangeOfPeriod } from "./utils/records"

import styles from "./analytics.module.less"

// Deflect Analytics: bank inquiries, match rate, and what Digital Receipt deflects. Same shape as
// the Prevent overview — calendar + Compare + Program on top, clickable KPI tabs over the trend,
// then full-width and 2-up panels.
//
// Every panel is a section with its own endpoint, the way Health → Overview works. The page owns
// the scope (window, comparison, programs) and hands each section the fetcher of the scope it
// reads — getCompare where the section shows a comparison, getBase where it only needs the
// window, getQueue for the live queues. The section asks with its own params and reads its own
// answer from the slice.
const DeflectAnalyticsPage = () => {
  const dispatch = useDispatch()
  const { getQueue, getBase, getCompare, comparing } = useDeflectScope()
  const { period } = useSelector(selectDeflectFilters)
  const businessAccountId = useSelector((state) => state.auth.userInfo?.current_business_account_id)
  const kpis = useSelector(selectDeflectSection(DEFLECT_SECTION.KPIS))
  const kpisFetching = useSelector(selectDeflectFetching(DEFLECT_SECTION.KPIS))

  const range = useMemo(() => rangeOfPeriod(period), [period])

  // No lookup inside the selected window: one page-level hint (the calendar above stays
  // reachable to widen it) instead of every panel's own empty. Decided only once kpis/ has
  // answered, so while it loads the panels mount and ask in parallel with it.
  const nothingInWindow = !kpisFetching && kpis?.summary?.lookups_count === 0

  useEffect(() => {
    document.title = "Deflect Analytics - Chargeback"
  }, [])

  const accountRef = useRef(businessAccountId)
  useEffect(() => {
    if (accountRef.current === businessAccountId) return
    accountRef.current = businessAccountId
    dispatch(resetDeflectData())
  }, [businessAccountId, dispatch])

  return (
    <div className={styles.page}>
      <Row gutter={[16, 16]} align="stretch">
        <Col span={24}>
          <DeflectFilters />
        </Col>
        {/* A business-account switch is a different page of data, not an update to this one:
            keying the sections on the account remounts them, and every one asks again. */}
        <Fragment key={businessAccountId}>
          <Col span={24}>
            <KpiTabs getData={getCompare} comparing={comparing} />
          </Col>
          <Col span={24}>
            <QueueCards getData={getQueue} />
          </Col>
          {nothingInWindow ? (
            <Col span={24}>
              <EmptyComponent type="no_lookups_in_range" />
            </Col>
          ) : (
            <>
              <Col span={24}>
                <LookupFlow getData={getBase} />
              </Col>
              <Col xs={24} lg={12}>
                <ChannelDonut getData={getCompare} />
              </Col>
              <Col xs={24} lg={12}>
                <CountriesPanel getData={getCompare} range={range} />
              </Col>
              <Col xs={24} lg={12}>
                <ProgramPanel getData={getBase} />
              </Col>
              <Col xs={24} lg={12}>
                <MatchRateMatrix getData={getBase} />
              </Col>
              <Col span={24}>
                <RepeatCardholdersPanel getData={getCompare} />
              </Col>
              <Col span={24}>
                <ChannelTrendsChart getData={getBase} />
              </Col>
              <Col span={24}>
                <RecentLookups getData={getBase} />
              </Col>
            </>
          )}
        </Fragment>
      </Row>
    </div>
  )
}

export default DeflectAnalyticsPage
